export const formatTextToHtml = (text: string): string => text ? text.replace(/\n/gu, '<br>') : '';

/**
 * Escapes raw control characters (newlines, carriage returns, tabs) that appear
 * *inside* JSON string literals. Some back-end payloads embed unescaped line
 * breaks in string values, which is invalid JSON and makes `JSON.parse` throw.
 * Structural whitespace (control chars between tokens) is left untouched so
 * pretty-printed JSON keeps parsing correctly. The inner text is preserved
 * exactly: `\n` is re-materialized as a real line break once parsed.
 */
const escapeControlCharsInJsonStrings = (value: string): string =>
    value.replace(/"(?:[^"\\]|\\.)*"/gu, match => match
        .replace(/\n/gu, '\\n')
        .replace(/\r/gu, '\\r')
        .replace(/\t/gu, '\\t'));

/**
 * Safely parses a JSON string into a typed object.
 * Returns `null` when the input is empty or cannot be parsed, so callers can
 * fall back gracefully instead of throwing. When the first parse fails, it
 * retries after repairing unescaped control characters embedded in string
 * values (a common back-end serialization issue), preserving the inner text.
 */
export const parseJsonString = <T>(value?: string | null): T | null => {
    if (!value) {
        return null;
    }

    try {
        const parsed: unknown = JSON.parse(value);
        return parsed as T;
    } catch {
        try {
            const repaired: unknown = JSON.parse(escapeControlCharsInJsonStrings(value));
            return repaired as T;
        } catch {
            return null;
        }
    }
};

export const getInitialsFromName = (name?: string): string => (name ?? '').trim()
    .slice(0, 2)
    .toUpperCase();

/* eslint-disable id-length */
/* eslint-disable @typescript-eslint/no-magic-numbers */
/**
 * Returns a contrasting text color (#282829 or #FCFCFC) for a given hex background color.
 * Expects a 6-digit hex string prefixed with '#' (e.g. '#FF5733').
 */
export const getContrastColor = (bgColor: string): string => {
    const r = parseInt(bgColor.slice(1, 3), 16);
    const g = parseInt(bgColor.slice(3, 5), 16);
    const b = parseInt(bgColor.slice(5, 7), 16);

    const luminance = ((0.299 * r) + (0.587 * g) + (0.114 * b)) / 255;
    return luminance > 0.5 ? '#282829' : '#FCFCFC';
};
/* eslint-enable id-length */
/* eslint-enable @typescript-eslint/no-magic-numbers */
